import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingButtons from '@/components/feature/FloatingButtons';
import GalleryHero from './components/GalleryHero';
import GallerySectionNav from './components/GallerySectionNav';
import GalleryGrid from './components/GalleryGrid';
import GalleryCTA from './components/GalleryCTA';
import { db } from '@/lib/firebase';
import { collection, onSnapshot, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';

export default function GalleryPage() {
  const [sections, setSections] = useState<any[]>([]);
  const [images, setImages] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Gallery | Bucara Burger – Colombian Food Truck Boise Idaho';

    const unsubImages = onSnapshot(doc(db, 'settings', 'landing_images'), (docSnap) => {
      if (docSnap.exists()) {
        setImages(docSnap.data());
      }
    });

    // Fetch Categories and Photos to build sections
    const unsubCats = onSnapshot(query(collection(db, 'gallery_categories'), orderBy('name')), (catSnap) => {
      const cats = catSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), title: doc.data().name }));
      
      const unsubPhotos = onSnapshot(query(collection(db, 'gallery'), orderBy('createdAt', 'desc')), (photoSnap) => {
        const photos = photoSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Build sections dynamically
        const gallerySections = cats.map(cat => ({
          ...cat,
          images: photos.filter((p: any) => p.categoryId === cat.id).map((p: any) => ({
            id: p.id,
            src: p.imageUrl,
            alt: p.name,
            span: 'normal' // Default span for dynamic content
          }))
        })).filter(section => section.images.length > 0);

        setSections(gallerySections);
        setLoading(false);
      });

      return () => unsubPhotos();
    });

    return () => {
      unsubCats();
      unsubImages();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="text-[#F57F25] animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* SEO structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ImageGallery',
            name: 'Bucara Burger Gallery',
            description: 'Photo gallery of Bucara Burger – Colombian food truck in Boise Idaho. Featuring gourmet burgers, Colombian hot dogs, empanadas, arepas, and food truck experience.',
            url: 'https://bucaraburger.com/gallery',
            about: {
              '@type': 'FoodEstablishment',
              name: 'Bucara Burger',
              servesCuisine: 'Colombian',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Boise',
                addressRegion: 'ID',
                addressCountry: 'US',
              },
            },
          }),
        }}
      />

      <Navbar />
      <GalleryHero image={images?.galleryHeroImage} />
      <GallerySectionNav sections={sections} />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        {/* Instagram follow strip */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#111111] border border-white/5 rounded-xl px-6 py-4 mb-12">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#F57F25] to-[#e06d15] rounded-lg">
              <i className="ri-instagram-line text-black text-lg"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm">Follow us on Instagram</p>
              <p className="text-white/40 text-xs">@bucara__burger · Burgers &amp; Latin food in Boise</p>
            </div>
          </div>
          <a
            href="https://instagram.com/bucara__burger"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#F57F25] hover:bg-[#e06d15] text-black font-bold text-xs px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-instagram-line text-sm"></i>
            </div>
            Follow @bucara__burger
          </a>
        </div>

        {/* Gallery sections */}
        {sections.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/20 uppercase font-black tracking-widest">No photos published yet</p>
          </div>
        ) : (
          sections.map((section) => (
            <GalleryGrid key={section.id} section={section} />
          ))
        )}

        {/* CTA */}
        <GalleryCTA image={images?.galleryCTAImage} />
      </main>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
